import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Calendar, CheckCircle, AlertCircle, Loader2, PoundSterling, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

/**
 * Calculate months between now and a deadline date
 */
const calculateMonthsUntilDeadline = (deadlineDate) => {
  if (!deadlineDate) return null;
  
  const now = new Date();
  const deadline = new Date(deadlineDate);
  
  // Calculate months difference
  let months = (deadline.getFullYear() - now.getFullYear()) * 12;
  months += deadline.getMonth() - now.getMonth();
  
  // If we're past the 1st of this month, don't count this month
  if (now.getDate() > 1) {
    months -= 1;
  }
  
  return Math.max(1, months); // At least 1 month
};

const DancerPaymentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [card, setCard] = useState(null);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [paymentMode, setPaymentMode] = useState('installments'); // 'installments' or 'oneoff'

  // Calculate suggested monthly payment
  const suggestedPayment = useMemo(() => {
    if (!shareData) return null;
    
    const remaining = shareData.share?.remaining || 0;
    if (remaining <= 0) return null;
    
    // Try to use payment_deadline, otherwise use event start_date - 1 month
    let deadline = shareData.booking?.payment_deadline;
    if (!deadline && shareData.event?.start_date) {
      const startDate = new Date(shareData.event.start_date);
      startDate.setMonth(startDate.getMonth() - 1);
      deadline = startDate.toISOString();
    }
    
    if (!deadline) return null;
    
    const months = calculateMonthsUntilDeadline(deadline);
    if (!months) return null;
    
    return {
      months,
      amount: Math.ceil((remaining / months) * 100) / 100, // Round up to nearest penny
      deadline: new Date(deadline).toLocaleDateString('en-GB', { 
        day: 'numeric', month: 'long', year: 'numeric' 
      })
    };
  }, [shareData]);

  // Load share details and handle service worker
  useEffect(() => {
    // Completely unregister service workers on payment page to avoid Square SDK conflicts
    const clearServiceWorkers = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          console.log('[DancerPayment] Unregistering', registrations.length, 'service worker(s)');
          for (const registration of registrations) {
            await registration.unregister();
          }
        } catch (err) {
          console.log('[DancerPayment] Service worker cleanup error:', err);
        }
      }
    };
    
    clearServiceWorkers();
    
    const fetchShareDetails = async () => {
      try {
        console.log('[DancerPayment] Fetching share details...');
        const response = await fetch(`${API_URL}/api/dancer-payments/pay/${token}`);
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Payment link not found');
        }
        
        const data = await response.json();
        console.log('[DancerPayment] Share data loaded successfully');
        setShareData(data);
        
        // Set default monthly amount based on suggested payment
        const remaining = data.share.remaining || 0;
        if (remaining > 0) {
          setMonthlyAmount(remaining.toFixed(2));
        }
      } catch (err) {
        console.error('[DancerPayment] Fetch error:', err);
        setError(err.message || 'Failed to load payment details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchShareDetails();
  }, [token]);

  // Update monthly amount when suggested payment is calculated
  useEffect(() => {
    if (suggestedPayment && shareData) {
      setMonthlyAmount(suggestedPayment.amount.toFixed(2));
      setPaymentMode('installments');
    }
  }, [suggestedPayment, shareData]);

  // Load Square Web Payments SDK
  useEffect(() => {
    if (!shareData || shareData.share.card_on_file_id) return;

    const loadSquare = async () => {
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://web.squarecdn.com/v1/square.js';
      script.async = true;
      script.onload = () => setSquareLoaded(true);
      script.onerror = () => setError('Failed to load payment system');
      document.body.appendChild(script);
    };

    loadSquare();
  }, [shareData]);

  // Initialize Square card payment
  useEffect(() => {
    if (!squareLoaded || !shareData || shareData.share.card_on_file_id) return;

    const initializeCard = async () => {
      try {
        const payments = window.Square.payments(
          shareData.square_app_id,
          shareData.square_location_id
        );

        const cardInstance = await payments.card();
        await cardInstance.attach('#card-container');
        setCard(cardInstance);
      } catch (err) {
        console.error('Failed to initialize card:', err);
        setError('Failed to initialize payment form');
      }
    };

    initializeCard();
  }, [squareLoaded, shareData]);

  const handleSetupPlan = useCallback(async () => {
    if (!card) {
      toast.error('Payment form not ready');
      return;
    }

    const amount = parseFloat(monthlyAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid monthly amount');
      return;
    }

    if (amount > shareData.share.remaining) {
      toast.error(`Amount cannot exceed £${shareData.share.remaining.toFixed(2)}`);
      return;
    }

    setProcessing(true);

    try {
      console.log('[DancerPayment] Starting tokenization...');
      let tokenResult;
      try {
        tokenResult = await card.tokenize();
        console.log('[DancerPayment] Tokenization result:', tokenResult?.status);
      } catch (tokenError) {
        console.error('[DancerPayment] Tokenization error:', tokenError);
        // Show more specific error for mobile users
        toast.error('Card processing failed. Please try a different browser or contact support.');
        setProcessing(false);
        return;
      }
      
      if (tokenResult.status !== 'OK') {
        const errorMessage = tokenResult.errors?.[0]?.message || 'Card verification failed';
        console.error('[DancerPayment] Tokenization failed:', tokenResult.errors);
        toast.error(errorMessage);
        setProcessing(false);
        return;
      }

      console.log('[DancerPayment] Card tokenized successfully, sending to backend...');
      
      let response;
      // Use a slight delay after Square tokenization to help Safari
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        response = await fetch(`${API_URL}/api/dancer-payments/pay/${token}/setup-plan`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            monthly_amount: amount,
            card_nonce: tokenResult.token,
            verification_token: tokenResult.verificationToken,
            pay_now: paymentMode === 'oneoff'
          })
        });
      } catch (fetchError) {
        console.error('[DancerPayment] Fetch error:', fetchError);
        // Try alternative approach for Safari
        toast.error('Browser blocked the request. Please try using Chrome browser instead.');
        setProcessing(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('[DancerPayment] JSON parse error:', jsonError);
        toast.error('Server returned invalid response. Please try again.');
        setProcessing(false);
        return;
      }
      
      console.log('[DancerPayment] Backend response:', response.status);

      if (!response.ok) {
        toast.error(data.detail || 'Failed to set up payment plan');
        setProcessing(false);
        return;
      }

      if (paymentMode === 'oneoff') {
        toast.success('Payment successful!');
      } else {
        toast.success('Payment plan set up! First payment will be taken on the 1st of next month.');
      }
      
      // Refresh share data
      try {
        const refreshResponse = await fetch(`${API_URL}/api/dancer-payments/pay/${token}`);
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setShareData(refreshData);
        }
      } catch (refreshErr) {
        console.log('[DancerPayment] Refresh failed, but payment succeeded');
      }
    } catch (err) {
      console.error('[DancerPayment] Unexpected error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [card, monthlyAmount, shareData, token, paymentMode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Link Error</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, booking, event } = shareData;
  const isCompleted = share.status === 'completed';
  const hasPaymentPlan = share.card_on_file_id || share.monthly_amount;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Your Payment Share</h1>
          <p className="text-gray-600 mt-1">
            {event?.title || booking?.event_title || 'Weekend Event'}
          </p>
        </div>

        {/* Share Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-purple-600" />
              Payment Summary
            </CardTitle>
            <CardDescription>
              Hi {share.dancer_name}, here's your payment share details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Your Share</p>
                <p className="text-2xl font-bold text-gray-900">£{share.share_amount?.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Amount Paid</p>
                <p className="text-2xl font-bold text-green-600">£{share.amount_paid?.toFixed(2)}</p>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700">Remaining Balance</p>
              <p className="text-3xl font-bold text-purple-900">£{share.remaining?.toFixed(2)}</p>
            </div>

            {booking && (
              <div className="text-sm text-gray-600 pt-2 border-t">
                <p><strong>Booking Reference:</strong> {booking.booking_id}</p>
                <p><strong>Booked by:</strong> {booking.customer_name}</p>
              </div>
            )}

            {event && (
              <div className="text-sm text-gray-600">
                <p><strong>Venue:</strong> {event.venue}</p>
                {event.start_date && (
                  <p><strong>Date:</strong> {new Date(event.start_date).toLocaleDateString('en-GB', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completed Status */}
        {isCompleted && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <AlertDescription className="text-green-800">
              Your payment share is fully paid. Thank you!
            </AlertDescription>
          </Alert>
        )}

        {/* Active Payment Plan */}
        {hasPaymentPlan && !isCompleted && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Your Payment Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Payment</span>
                <span className="font-semibold">£{share.monthly_amount?.toFixed(2)}</span>
              </div>
              
              {share.next_payment_date && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Next Payment</span>
                  <span className="font-semibold">
                    {new Date(share.next_payment_date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {share.card_last4 && (
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>Card on file</span>
                  <span>•••• {share.card_last4}</span>
                </div>
              )}

              {/* Payment History */}
              {share.payment_history?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-gray-900 mb-2">Payment History</h4>
                  <div className="space-y-2">
                    {share.payment_history.map((payment, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {new Date(payment.date).toLocaleDateString('en-GB')}
                        </span>
                        <span className="font-medium text-green-600">
                          £{payment.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Setup Payment Plan Form */}
        {!hasPaymentPlan && !isCompleted && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-600" />
                Pay Your Share
              </CardTitle>
              <CardDescription>
                Choose how you'd like to pay and enter your card details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Mode Toggle */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMode('installments');
                    if (suggestedPayment) {
                      setMonthlyAmount(suggestedPayment.amount.toFixed(2));
                    }
                  }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    paymentMode === 'installments' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">Monthly Installments</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {suggestedPayment 
                      ? `${suggestedPayment.months} payments of £${suggestedPayment.amount.toFixed(2)}`
                      : 'Spread the cost monthly'
                    }
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMode('oneoff');
                    setMonthlyAmount(share.remaining?.toFixed(2) || '0');
                  }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    paymentMode === 'oneoff' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">Pay in Full</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    One payment of £{share.remaining?.toFixed(2)}
                  </p>
                </button>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {paymentMode === 'oneoff' ? 'Payment Amount' : 'Monthly Payment Amount'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                  <Input
                    type="number"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(e.target.value)}
                    className="pl-7"
                    placeholder="50.00"
                    min="1"
                    max={share.remaining}
                    step="0.01"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Min £1 • Max £{share.remaining?.toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Card Details
                </label>
                <div 
                  id="card-container" 
                  className="border rounded-md p-3 min-h-[50px] bg-white"
                />
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-800 text-sm">
                  {paymentMode === 'oneoff' ? (
                    <>Your payment of <strong>£{parseFloat(monthlyAmount)?.toFixed(2) || '0.00'}</strong> will be taken today and your share will be fully paid.</>
                  ) : (
                    <>
                      <strong>No payment today.</strong> Your card will be saved and <strong>£{parseFloat(monthlyAmount)?.toFixed(2) || '0.00'}</strong> will be taken on the <strong>1st of each month</strong>, starting next month, until fully paid.
                    </>
                  )}
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleSetupPlan}
                disabled={processing || !card}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : paymentMode === 'oneoff' ? (
                  `Pay £${parseFloat(monthlyAmount)?.toFixed(2) || '0.00'} Now`
                ) : (
                  `Set Up Monthly Payments of £${parseFloat(monthlyAmount)?.toFixed(2) || '0.00'}`
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                {paymentMode === 'oneoff' 
                  ? 'Your payment will be processed securely. Contact the booking holder if you have any questions.'
                  : 'Your card will be saved securely. First payment on the 1st of next month. Contact the booking holder to cancel.'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DancerPaymentPage;
